import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CmxClientService, State, getVersion } from '@cemex/cmx-client-layout';
import {
  Router,
  NavigationEnd,
  ActivatedRoute,
  NavigationStart,
  NavigationCancel,
  NavigationError,
} from '@angular/router';
import { distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { AnalyticsTrackingService } from './services/analytics-tracking.service';
import { AppInsightsService } from './services/app-insights.service';
import { Subject } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { environment } from 'environments/environment';
import { CobrowseFeatureService } from './services/cobrowse-feature.service';
import { ExpirationTimeService } from './shared/expiration-time/expiration-time.service';
import { SessionService } from '@cemex-core/angular-services-v8';

@Component({
  selector: 'app-root',
  template: `
    <router-outlet></router-outlet>
    <cwc-cobrowse
      *ngIf="showCobrowse"
      [license]="cobrowseKey"
      [codeMessage]="'views.cobrowse.code_message' | translate"
      [requestAccessTitle]="'views.cobrowse.request_access_title' | translate"
      [requestAccessMessage]="'views.cobrowse.request_access_message' | translate"
      [remoteAccessTitle]="'views.cobrowse.remote_control_title' | translate"
      [remoteControlMessage]="'views.cobrowse.remote_control_message' | translate"
      [allowButtonText]="'views.cobrowse.allow_button_text' | translate"
      [denyButtonText]="'views.cobrowse.deny_button_text' | translate"
      [endSessionButtonText]="'views.cobrowse.end_session_button_text' | translate"
    ></cwc-cobrowse>
  `,
})
export class AppComponent implements OnInit, OnDestroy {
  // private customerId: string;
  private externalAuth = new Subject();
  public cobrowseKey = environment.cobrowseKey;
  public showCobrowse = false;
  private destroyed$ = new Subject();
  private version = 3;
  constructor(
    private client: CmxClientService,
    private router: Router,
    private route: ActivatedRoute,
    private analyticsTrackingService: AnalyticsTrackingService,
    private appInsights: AppInsightsService,
    private cobrowseService: CobrowseFeatureService,
    private store: Store<State>,
    private expirationTimeService: ExpirationTimeService,
    private sessionService: SessionService,
  ) {
    this.route.queryParams.pipe(filter((params) => Object.keys(params).length > 0)).subscribe((params) => {
      if (params.returnUrl) {
        if (params.returnUrl.indexOf('external=true') > -1) {
          // Is calling from external source (Console)
          // Listen for token and customer
          const afterAuthPage = window.location.origin + params.returnUrl;
          this.externalAuth.pipe(take(1)).subscribe((auth) => {
            if (auth) {
              // Reload to go to return url
              parent.postMessage('authenticated', '*');
              window.location.href = afterAuthPage;
            }
          });
        }
      }
    });
    this.router.events.subscribe((event) => {
      try {
        if (event instanceof NavigationEnd) {
          const newrelic = window['newrelic'];
          if (newrelic) {
            try {
              let username = this.sessionService.username;
              if (!username) {
                const profile = this.sessionService.profile;
                username = profile ? profile.userAccount : username;
              }
              newrelic.setCustomAttribute('cmx_username', username);
            } catch (error) {
              console.warn(error);
            }
            const i = newrelic.interaction();
            i.setName(event.urlAfterRedirects);
            i.save();
          } else {
            console.warn('New Relic script not loaded');
          }
        }
      } catch (error) {
        console.warn(error);
      }
    });
    this.analyticsTrackingService.initAnalyticsTracking();
    this.cobrowseService.getCobrowseFeatureValue().subscribe((showCobrowse) => {
      this.showCobrowse = showCobrowse;
    });
    this.version = window['CX_VERSION'] && window['CX_VERSION'] === '2' ? 2 : 3;
    this.client.setVersion(this.version);
  }

  @HostListener('window:message', ['$event'])
  onMessage(e) {
    if (!this.sessionService.dataSession) {
      // Only activate when there is no token data
      try {
        if (e.data) {
          const obj = JSON.parse(e.data);
          if (obj.token) {
            this.sessionService.updateDataSession(obj.token);
            this.externalAuth.next(true);
          }
        }
      } catch (ex) {}
    }
  }

  ngOnInit() {
    this.appInsights.loadAppInsights();
    this.router.events.pipe(distinctUntilChanged()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.client.showGlobalLoader();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.client.hideGlobalLoader();
      }
    });
    this.client.init();
    // observe the version of the navbar
    this.store.pipe(select(getVersion), takeUntil(this.destroyed$)).subscribe((version: number) => {
      this.version = version;
    });
    this.expirationTimeService.pathsVisited = [this.router.url];
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * @description This method is used when setting the current global nav version
   * @param ev Event
   */
  @HostListener('document:changeNavVersion', ['$event'])
  changeNavVersion(ev: CustomEvent) {
    this.client.setVersion(this.version === 2 ? 3 : 2);
  }
}
